import { keyBy } from 'lodash';
import {
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFilter,
} from 'api/dashboard/widget';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldFilter } from 'components/organisms/tasks/filters';
import {
  GroupCriteria,
  GroupedFilter,
} from 'components/organisms/tasks/grouped-filter-popover';
import { FilterGroupType } from 'components/pages/types';
import { logger } from 'services';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'utils/simple-search-filter/simple-search-filter';
import { defaultGroupedFilter } from '../organisms/tasks/utils';
import { ApiFilterTransformer } from './api-filter-transformer';
import { GroupFieldsMeta } from './filter-transformer';

export const to_grouped_filter = (
  filter: GroupedFilter,
): SimpleSearchFilter => ({
  nativeFields: filter.groupCriteria
    .filter(({ criteria }) => FieldFilter.isValid(criteria))
    .filter(({ criteria }) => !criteria.field.userCreated)
    .map(ApiGroupFilter.toNativeFieldFilterFormatForGroups),
  customFields: filter.groupCriteria
    .filter(({ criteria }) => FieldFilter.isValid(criteria))
    .filter(({ criteria }) => criteria.field.userCreated)
    .map(ApiGroupFilter.toCustomFieldFilterFormatForGroups),
  match: filter.type,
});

const tryParseNativeFieldFilterForGroup = (
  filter: NativeFieldFilter,
  group: FilterGroupType,
  lookups: GroupFieldsMeta,
): GroupCriteria | null => {
  const field = lookups.find(({ id }) => id === group)?.fields[filter.name];

  if (!field) {
    return null;
  }

  try {
    return {
      id: group,
      criteria: ApiFilter.parse(filter, field),
    };
  } catch (err) {
    logger.error('failed to parse criteria', { filter, error: err as Error });

    return null;
  }
};

const tryParseCustomFieldFilterForGroup = (
  filter: CustomFieldFilter,
  group: FilterGroupType,
  lookups: GroupFieldsMeta,
): GroupCriteria | null => {
  const byIdLookup = lookups
    .filter((x) => x.id === group)
    .map((x) => ({
      ...x,
      fields: keyBy(Object.values(x.fields), 'fieldId'),
    }));

  const field = byIdLookup[0]?.fields[filter.fieldId];

  if (!field) {
    return null;
  }

  try {
    return {
      id: group,
      criteria: ApiFilter.parse(filter, field),
    };
  } catch (err) {
    logger.error('failed to parse criteria', { filter, error: err as Error });

    return null;
  }
};

export const from_grouped_filter =
  (lookups: GroupFieldsMeta, defaultGroup: FilterGroupType) =>
  (query: SimpleSearchFilter): GroupedFilter =>
    lookups.every((lookup) => Object.keys(lookup).length === 0)
      ? defaultGroupedFilter(defaultGroup)
      : {
          type: query.match ?? QueryCombinerType.All,
          defaultGroup,
          groupCriteria: [
            ...(query.nativeFields ?? [])
              .map((field) =>
                tryParseNativeFieldFilterForGroup(
                  field,
                  field.sourceType
                    ? (field.sourceType as unknown as FilterGroupType)
                    : defaultGroup,
                  lookups,
                ),
              )
              .filter((x) => x != null)
              .map((x) => x as GroupCriteria),
            ...(query.customFields ?? [])
              .map((field) =>
                tryParseCustomFieldFilterForGroup(
                  field,
                  field.sourceType
                    ? (field.sourceType as unknown as FilterGroupType)
                    : defaultGroup,
                  lookups,
                ),
              )
              .filter((x) => x != null)
              .map((x) => x as GroupCriteria),
          ],
        };

export const getApiGroupedFilterTransformer = (
  lookup: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
): ApiFilterTransformer<GroupedFilter> => ({
  from: (simpleSearchFilter) =>
    from_grouped_filter(lookup, defaultGroup)(simpleSearchFilter),
  to: to_grouped_filter,
});
